import {
  SourceEntityRecordStatus,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { User } from '@principle-theorem/principle-core';
import { ServiceTypeModality } from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  BaseStafferDestinationEntity,
  IStafferJobData,
  IStafferMigrationData,
} from '../../../destination/entities/staff';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticeProvider,
  ProviderSourceEntity,
} from '../../source/entities/providers';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import { CorePracticeStaffToUserMappingHandler } from '../mappings/staff-to-user';

export const STAFF_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'staff',
    label: 'Staff',
    description: `Roles will need to be assigned to practitioners with the "can conduct appointments" permission before any further migrating can be done.

    In the case of multiple practice migrations where there are staff accounts for the same person in each location, the user will need to be created beforehand and mapped to the CorePractice staff accounts using the "Multi-staffer to User" custom mapping.`,
  },
});

export class StafferDestinationEntity extends BaseStafferDestinationEntity<CorePracticeProvider> {
  destinationEntity = STAFF_DESTINATION_ENTITY;
  stafferSourceEntity = new ProviderSourceEntity();
  stafferCustomMapping = new CorePracticeStafferMappingHandler();
  staffToUserCustomMapping = new CorePracticeStaffToUserMappingHandler();

  override sourceEntities = {
    staff: new ProviderSourceEntity(),
  };

  buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IStafferJobData<CorePracticeProvider>
  ):
    | IStafferMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord) {
    if (data.stafferRecord.record.status === SourceEntityRecordStatus.Invalid) {
      return this._buildErrorResponse(
        data.stafferRecord.record,
        'Source staffer is invalid'
      );
    }

    const sourceStafferId = this.sourceEntities.staff
      .getSourceRecordId(data.stafferRecord.data)
      .toString();

    const user = User.init({
      name: `${data.stafferRecord.data.firstName} ${data.stafferRecord.data.providerName}`,
      email: '',
      practices: migration.configuration.practices.map(
        (practice) => practice.ref
      ),
      brands: [migration.configuration.brand.ref],
      isEnabled: false,
    });

    return {
      sourceStafferId,
      user,
      stafferDetails: {
        providerNumber: data.stafferRecord.data.providerNumber,
        providerModality: ServiceTypeModality.GeneralDentist,
      },
    };
  }
}
